/**
 * Intakes slice (PX-W7.1, TASK-A30F4433) — the Interviews page's backing
 * state: the version's intakes, a selected session's transcript + analysis,
 * and the org-admin capture surface (meeting channels, external captures,
 * capture links).
 *
 * Three read thunks, the PX-W6.3 panel-owns-its-fetch idiom:
 *  - `openIntakes` — the primary list read. A 403/404 lands in `blocked`
 *    (renders `ProjectBlockedState`); every other failure lands in `error`
 *    (renders `ErrorPanel`) — exactly one is non-null, never both (INV-1.3).
 *  - `loadSession` — a selected session's transcript + analysis together
 *    (the session route addresses both from one `{intakeId, assigneeId}`
 *    pair, decision 1 of the plan).
 *  - `loadCaptureSurface` — the three org-admin capture reads via
 *    `Promise.allSettled`; a 403 on ANY of them sets `captureForbidden` and
 *    leaves the page's `blocked` state untouched (decision 3 / AC-6): the
 *    capture panel renders its OWN `ForbiddenPanel`, the rest of the page
 *    keeps working.
 *
 *    PX-W7.1 fix cycle 1 (GRP-B3D9F416, findings 3/4) widened this beyond the
 *    403 branch: a non-403 failure on ONE of the three reads used to fold
 *    into a single `captureError` that blanked the WHOLE panel, hiding two
 *    reads that had actually succeeded — each read now carries its OWN error
 *    field (`channelsError` / `externalCapturesError` / `captureLinksError`),
 *    so a failed capture-links read never hides a working channels block.
 *    `captureLinks` is also now a TRI-STATE (`null` = not read for the
 *    current version yet, `CaptureLinkRow[]` = a completed read, possibly
 *    empty) rather than a bare array, so "no capture link minted" is never
 *    shown before a real read completed — the zero-count sentence used to
 *    print on mount (before ANY read) and whenever no version was known,
 *    because a skipped read and a completed-empty read looked identical.
 *
 * Realtime is refetch-on-invalidate on the EXISTING `realtime/invalidateActivity`
 * action (no new nonce family, `realtimeSlice.ts` untouched) — this slice
 * `addCase`s a typed alias of that action type and guards on the frame's
 * `projectId`, exactly as `activityFeedSlice.activityBusFrame` does.
 */

import { createAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit'

import { isApiError, toApiError } from '@/services'
import {
  fetchFailed,
  fetchStarted,
  fetchSucceeded,
  initialFetchCache,
  invalidate,
  markServerChanged,
  shouldFetch,
  type FetchCache
} from '@/store/fetchCache'
import { intakesService } from '../services/intakesService.http'
import type {
  CaptureLinkRow,
  ConversationSnapshot,
  ExternalCaptureRow,
  FeatureBlocked,
  IntakeRow,
  KeyedFeatureError,
  MeetingChannelSurface,
  SessionAnalysis
} from '../types'

/** A rejected read's payload — always carries the real HTTP status (INV-1.3's routing key). */
interface IntakesFailure {
  status: number
  scope?: string
  code?: string
  message: string
}

const toIntakesFailure = (error: unknown, scope?: string): IntakesFailure => {
  const apiError = isApiError(error) ? error : toApiError(error)
  return { status: apiError.status, scope, code: apiError.backendKey, message: apiError.message }
}

export interface IntakesState extends FetchCache {
  projectId: string | null
  activeVersionId: string | null
  intakes: IntakeRow[]
  selected: { intakeId: string; assigneeId: string } | null
  transcript: ConversationSnapshot | null
  analysis: SessionAnalysis | null
  sessionLoading: boolean
  sessionError: KeyedFeatureError | null
  capture: {
    channels: MeetingChannelSurface | null
    externalCaptures: ExternalCaptureRow[]
    /** Tri-state (finding 4): `null` until a completed read for THIS version; an array (possibly empty) once one has. */
    captureLinks: CaptureLinkRow[] | null
  } | null
  captureLoading: boolean
  /**
   * The `orgId:projectId:versionId` key currently IN FLIGHT (finding 4). Only
   * a dispatch for this SAME key is deduped — a different key (e.g. the
   * version-resolved re-dispatch that follows the mount-time `null`-version
   * dispatch) is never dropped just because another key's request has not
   * settled yet.
   */
  captureRequestedKey: string | null
  /**
   * The key the capture surface was last loaded for CLEANLY (no per-read
   * error, no forbidden) — the retry/mount guard. A key that finished with
   * any error is deliberately left un-cached here so the retry control (and
   * the next mount) can actually re-attempt it, rather than silently no-op
   * against a "loaded" key that never succeeded.
   */
  captureLoadedKey: string | null
  /** Per-sub-read failure (finding 3b) — a failed read never blanks the other two. */
  channelsError: KeyedFeatureError | null
  externalCapturesError: KeyedFeatureError | null
  captureLinksError: KeyedFeatureError | null
  /** True on a 403 from ANY of the three capture reads (decision 3 / AC-6). */
  captureForbidden: boolean
  error: KeyedFeatureError | null
  blocked: FeatureBlocked | null
  invalidationNonce: number
}

const initialState: IntakesState = {
  projectId: null,
  activeVersionId: null,
  intakes: [],
  selected: null,
  transcript: null,
  analysis: null,
  sessionLoading: false,
  sessionError: null,
  capture: null,
  captureLoading: false,
  captureRequestedKey: null,
  captureLoadedKey: null,
  channelsError: null,
  externalCapturesError: null,
  captureLinksError: null,
  captureForbidden: false,
  error: null,
  blocked: null,
  invalidationNonce: 0,
  ...initialFetchCache
}

// ---------------------------------------------------------------------------
// Thunks
// ---------------------------------------------------------------------------

/** The version's intakes (AC-1). Guarded once per `(projectId, versionId)` (INV-1.6). */
export const openIntakes = createAsyncThunk<
  IntakeRow[],
  { orgId: string; projectId: string; versionId: string },
  { rejectValue: IntakesFailure }
>('intakes/open', async ({ orgId, projectId, versionId }, { rejectWithValue }) => {
  try {
    return await intakesService.listIntakes(orgId, projectId, versionId)
  } catch (error) {
    return rejectWithValue(toIntakesFailure(error, 'intakes'))
  }
}, {
  condition: ({ projectId, versionId }, { getState }) => {
    const { intakes } = getState() as { intakes: IntakesState }
    return shouldFetch(intakes, `${projectId}:${versionId}`)
  },
  // A vetoed open is still an OPEN: it dispatches `rejected` with
  // `meta.condition`, and the reducer re-asserts the project and version on screen
  // (TASK-3D6DB8B6 fix cycle 3 — see the rejected case). No request is sent.
  dispatchConditionRejection: true
})

/** A selected session's transcript + analysis, together (AC-2, AC-4, AC-5). */
export const loadSession = createAsyncThunk<
  { transcript: ConversationSnapshot; analysis: SessionAnalysis },
  { orgId: string; projectId: string; versionId: string; intakeId: string; assigneeId: string },
  { rejectValue: IntakesFailure }
>(
  'intakes/loadSession',
  async ({ orgId, projectId, versionId, intakeId, assigneeId }, { rejectWithValue }) => {
    try {
      const [transcript, analysis] = await Promise.all([
        intakesService.getSessionConversation(orgId, projectId, versionId, intakeId, assigneeId),
        intakesService.getSessionAnalysis(orgId, projectId, versionId, intakeId, assigneeId)
      ])
      return { transcript, analysis }
    } catch (error) {
      return rejectWithValue(toIntakesFailure(error, 'session'))
    }
  }
)

/** Fulfilled shape of {@link loadCaptureSurface} — never rejects (allSettled absorbs every failure). */
interface CaptureSurfaceResult {
  channels: MeetingChannelSurface | null
  channelsError: IntakesFailure | null
  externalCaptures: ExternalCaptureRow[]
  externalCapturesError: IntakesFailure | null
  /** Tri-state (finding 4): `null` when SKIPPED (no active version) or failed; an array once a real read completed. */
  captureLinks: CaptureLinkRow[] | null
  captureLinksError: IntakesFailure | null
  /** True when ANY of the three reads 403'd (org-admin-only surface, decision 3). */
  forbidden: boolean
}

/**
 * The org-admin capture surface: meeting channels + external captures
 * (org-scoped) and the version's capture links (project+version scoped —
 * `versionId: null` while no active version is known yet skips that one read,
 * leaving its tri-state at `null` rather than faking a completed empty read).
 *
 * The three reads are isolated (PX-W7.1 fix cycle 1, finding 3b): one read's
 * failure is reported on its OWN field and never discards the other two
 * reads' results.
 */
export const loadCaptureSurface = createAsyncThunk<
  CaptureSurfaceResult,
  { orgId: string; projectId: string; versionId: string | null }
>(
  'intakes/loadCaptureSurface',
  async ({ orgId, projectId, versionId }) => {
    const results = await Promise.allSettled([
      intakesService.listMeetingChannels(orgId),
      intakesService.listExternalCaptures(orgId),
      versionId === null
        ? Promise.resolve<CaptureLinkRow[] | null>(null)
        : intakesService.listCaptureLinks(orgId, projectId, versionId)
    ])
    const [channelsResult, externalResult, linksResult] = results as [
      PromiseSettledResult<MeetingChannelSurface>,
      PromiseSettledResult<ExternalCaptureRow[]>,
      PromiseSettledResult<CaptureLinkRow[] | null>
    ]

    let forbidden = false
    const noted = (result: PromiseSettledResult<unknown>): IntakesFailure | null => {
      if (result.status !== 'rejected') return null
      const failure = toIntakesFailure(result.reason, 'capture')
      if (failure.status === 403) {
        forbidden = true
        return null
      }
      return failure
    }
    const channelsError = noted(channelsResult)
    const externalCapturesError = noted(externalResult)
    const captureLinksError = noted(linksResult)

    return {
      channels: channelsResult.status === 'fulfilled' ? channelsResult.value : null,
      channelsError,
      externalCaptures: externalResult.status === 'fulfilled' ? externalResult.value : [],
      externalCapturesError,
      captureLinks: linksResult.status === 'fulfilled' ? linksResult.value : null,
      captureLinksError,
      forbidden
    }
  },
  {
    // No `state` generic (the pre-typed AppDispatch rejects narrower thunks —
    // the phaseGatesSlice.loadSnapshots precedent); getState cast locally.
    condition: ({ orgId, projectId, versionId }, { getState }) => {
      const { intakes } = getState() as { intakes: IntakesState }
      const key = `${orgId}:${projectId}:${versionId ?? ''}`
      // PX-W7.1 fix cycle 1 (finding 4) — dedupe only THIS key: the old
      // single `captureLoading` guard vetoed EVERY dispatch while any load
      // was in flight, which silently dropped the version-resolved
      // re-dispatch that follows the panel's initial `versionId: null` mount
      // read (still in flight when the real version resolves a beat later).
      // A key already loaded cleanly, or already in flight, is skipped; any
      // other key is always let through.
      return intakes.captureLoadedKey !== key && intakes.captureRequestedKey !== key
    }
  }
)

/**
 * Typed alias of `realtime/invalidateActivity` carrying the frame's
 * `projectId` — the SAME action type `EVENT_TYPE_MAP`'s `intake.*` rows
 * dispatch (the realtime slice's own reducer bumps its nonce and ignores the
 * payload; this slice applies the on-screen guard, the `activityFeedSlice`
 * pattern).
 */
export const intakesBusFrame = createAction<{ projectId: string | null }>('realtime/invalidateActivity')

// ---------------------------------------------------------------------------
// Slice
// ---------------------------------------------------------------------------

export const intakesSlice = createSlice({
  name: 'intakes',
  initialState,
  reducers: {
    /** Leave the workspace — resets every loaded bit of state (panel unmount). */
    intakesClosed(state) {
      state.projectId = null
      state.activeVersionId = null
      state.intakes = []
      state.selected = null
      state.transcript = null
      state.analysis = null
      state.sessionLoading = false
      state.sessionError = null
      state.capture = null
      state.captureLoading = false
      state.captureRequestedKey = null
      state.captureLoadedKey = null
      state.channelsError = null
      state.externalCapturesError = null
      state.captureLinksError = null
      state.captureForbidden = false
      state.error = null
      state.blocked = null
      state.invalidationNonce = 0
      invalidate(state)
    },
    /** Dismiss the primary list refusal (the retry control's neighbour). */
    intakesErrorDismissed(state) {
      state.error = null
    },
    /**
     * Re-arm the cache before a Retry re-dispatches `openIntakes` (c1-3 /
     * AC-2, the `requirementsSlice.requirementsRefetchArmed` precedent) — a
     * failed fetch leaves `invalidated: false` (fetchCache's rejected
     * contract) so a same-key re-dispatch would otherwise be silently
     * skipped by `shouldFetch`.
     */
    intakesRefetchArmed(state) {
      invalidate(state)
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(openIntakes.pending, (state, action) => {
        const { projectId, versionId } = action.meta.arg
        if (state.projectId !== projectId) {
          state.intakes = []
          state.selected = null
          state.transcript = null
          state.analysis = null
          state.sessionLoading = false
          state.sessionError = null
        }
        fetchStarted(state, `${projectId}:${versionId}`)
        state.projectId = projectId
        state.activeVersionId = versionId
        state.error = null
        state.blocked = null
      })
      .addCase(openIntakes.fulfilled, (state, action) => {
        // Key-scoped (TASK-3D6DB8B6): a superseded (project, version) reply writes nothing.
        if (!fetchSucceeded(state, `${action.meta.arg.projectId}:${action.meta.arg.versionId}`)) return
        const intakes = action.payload ?? []
        state.intakes = intakes
        // Fix cycle 4 (GRP-B04710BD, 7.1) — a realtime frame that arrives
        // because the SELECTED intake was deleted server-side must not leave
        // the transcript/analysis panels rendering for a session the new
        // list no longer contains (`IntakesPanel` gates those panels on
        // `selected !== null` alone, with no row left to deselect it).
        // `boardSlice` needs no equivalent because its "selected" is derived
        // from the list; here `selected` is a raw field that survives a list
        // replacement on its own. A payload that STILL contains the selected
        // id is a no-op here (cycle 3 pinned that the selection survives).
        if (state.selected !== null && !intakes.some((intake) => intake.id === state.selected?.intakeId)) {
          state.selected = null
          state.transcript = null
          state.analysis = null
          state.sessionLoading = false
          state.sessionError = null
        }
      })
      .addCase(openIntakes.rejected, (state, action) => {
        if (action.meta.condition) {
          // A VETOED open (TASK-3D6DB8B6 fix cycle 3, the projectDetail
          // precedent): this (project, version) load is in flight or fresh.
          // `intakesClosed` (StrictMode's between-effects cleanup, or a
          // remount mid-load) nulled both, so the frame guard and the
          // follow-up's on-screen check went dead. Re-assert them; the cache
          // is untouched and only an explicit open reaches this branch.
          state.projectId = action.meta.arg.projectId
          state.activeVersionId = action.meta.arg.versionId
          return
        }
        if (!fetchFailed(state, `${action.meta.arg.projectId}:${action.meta.arg.versionId}`)) return
        const failure = action.payload
        // INV-1.3 — exactly one of `blocked` / `error` is non-null, never both.
        if (failure !== undefined && (failure.status === 403 || failure.status === 404)) {
          state.blocked = { status: failure.status, code: failure.code, message: failure.message }
          state.error = null
        } else {
          state.error = failure ?? null
          state.blocked = null
        }
      })
      .addCase(loadSession.pending, (state, action) => {
        const { intakeId, assigneeId } = action.meta.arg
        state.selected = { intakeId, assigneeId }
        state.sessionLoading = true
        state.sessionError = null
        state.transcript = null
        state.analysis = null
      })
      .addCase(loadSession.fulfilled, (state, action) => {
        state.sessionLoading = false
        state.transcript = action.payload.transcript
        state.analysis = action.payload.analysis
      })
      .addCase(loadSession.rejected, (state, action) => {
        state.sessionLoading = false
        state.sessionError = action.payload ?? null
      })
      .addCase(loadCaptureSurface.pending, (state, action) => {
        const { orgId, projectId, versionId } = action.meta.arg
        state.captureLoading = true
        state.captureRequestedKey = `${orgId}:${projectId}:${versionId ?? ''}`
      })
      .addCase(loadCaptureSurface.fulfilled, (state, action) => {
        const { orgId, projectId, versionId } = action.meta.arg
        const key = `${orgId}:${projectId}:${versionId ?? ''}`
        // PX-W7.1 fix cycle 1 (finding 4) — a response for a key that is no
        // longer the one in flight is STALE (the version-resolved dispatch
        // ran past the mount-time `null`-version one): applying it would
        // flash the wrong version's capture data over the current one.
        if (state.captureRequestedKey !== key) return
        state.captureLoading = false
        state.captureRequestedKey = null
        state.capture = {
          channels: action.payload.channels,
          externalCaptures: action.payload.externalCaptures,
          captureLinks: action.payload.captureLinks
        }
        state.channelsError = action.payload.channelsError
        state.externalCapturesError = action.payload.externalCapturesError
        state.captureLinksError = action.payload.captureLinksError
        state.captureForbidden = action.payload.forbidden
        // Only a CLEAN cycle (no forbidden, no per-read error) is cached as
        // "loaded" — a key that finished with any error is left un-cached so
        // the retry control (and the next mount) can actually re-attempt it,
        // rather than the pre-fix behaviour where captureLoadedKey was set
        // regardless of errors and the retry button silently no-opped.
        const clean =
          !action.payload.forbidden &&
          action.payload.channelsError === null &&
          action.payload.externalCapturesError === null &&
          action.payload.captureLinksError === null
        state.captureLoadedKey = clean ? key : null
      })
      .addCase(loadCaptureSurface.rejected, (state, action) => {
        // c1-5 — `loadCaptureSurface`'s own body never rejects (allSettled
        // absorbs every read failure); this guards the case where the
        // mapping in `noted()` itself throws, so `captureLoading` never
        // sticks true and the retry condition never wedges shut.
        const { orgId, projectId, versionId } = action.meta.arg
        const key = `${orgId}:${projectId}:${versionId ?? ''}`
        if (state.captureRequestedKey !== key) return
        state.captureLoading = false
        state.captureRequestedKey = null
        state.channelsError = { status: 0, scope: 'capture', message: action.error.message ?? 'Unknown error' }
      })
      .addCase(intakesBusFrame, (state, action) => {
        const { projectId } = action.payload
        // INV-1.4 — a frame for another project (or with no project tuple) is a strict no-op.
        // A server change: mid-flight it earns one follow-up load (TASK-3D6DB8B6).
        if (projectId !== null && projectId === state.projectId) {
          state.invalidationNonce += 1
          markServerChanged(state)
          state.captureLoadedKey = null
        }
      })
  }
})

export const { intakesClosed, intakesErrorDismissed, intakesRefetchArmed } = intakesSlice.actions

/* ── Selectors ──────────────────────────────────────────────────────────── */

interface WithIntakes {
  intakes: IntakesState
}

export const selectIntakes = (s: WithIntakes) => s.intakes.intakes
export const selectSelectedSession = (s: WithIntakes) => s.intakes.selected
export const selectSessionTranscript = (s: WithIntakes) => s.intakes.transcript
export const selectSessionAnalysis = (s: WithIntakes) => s.intakes.analysis
export const selectSessionLoading = (s: WithIntakes) => s.intakes.sessionLoading
export const selectSessionError = (s: WithIntakes) => s.intakes.sessionError
export const selectCaptureSurface = (s: WithIntakes) => s.intakes.capture
export const selectCaptureLoading = (s: WithIntakes) => s.intakes.captureLoading
export const selectChannelsError = (s: WithIntakes) => s.intakes.channelsError
export const selectExternalCapturesError = (s: WithIntakes) => s.intakes.externalCapturesError
export const selectCaptureLinksError = (s: WithIntakes) => s.intakes.captureLinksError
export const selectCaptureForbidden = (s: WithIntakes) => s.intakes.captureForbidden
export const selectActiveIntakeVersionId = (s: WithIntakes) => s.intakes.activeVersionId
export const selectIntakesLoading = (s: WithIntakes) => s.intakes.loading
export const selectIntakesError = (s: WithIntakes) => s.intakes.error
export const selectIntakesBlocked = (s: WithIntakes) => s.intakes.blocked
export const selectIntakesNonce = (s: WithIntakes) => s.intakes.invalidationNonce
